import apiClient from './client';
import type {
  LocateTicket,
  LocateTicketDetail,
  LocateTicketListResponse,
  LocateTicketStats,
  LocateTicketFilters,
  CreateLocateTicketRequest,
  UpdateLocateTicketRequest,
  UpdateLocateTicketStatusRequest,
  LocateTicketStatus,
  LocateTicketType,
  ExcavationMethod,
  IowaStatusOption,
  PostIowaStatusRequest,
  IowaTicketResponseResult,
  LocateTicketPhoto,
  LocateTicketPhotoCategory,
  UpdateLocateTicketPhotoRequest,
} from '../types';

// ==================== Locate Tickets API ====================

export async function getLocateTickets(
  filters: LocateTicketFilters = {}
): Promise<LocateTicketListResponse> {
  const params = new URLSearchParams();

  if (filters.serviceAreaId) params.append('serviceAreaId', filters.serviceAreaId);
  if (filters.dropBuryId) params.append('dropBuryId', filters.dropBuryId);
  if (filters.constructionMilestoneId) params.append('constructionMilestoneId', filters.constructionMilestoneId);
  if (filters.permitApplicationId) params.append('permitApplicationId', filters.permitApplicationId);
  if (filters.assignedTechnicianId) params.append('assignedTechnicianId', filters.assignedTechnicianId);
  if (filters.status) params.append('status', filters.status);
  if (filters.statuses?.length) {
    filters.statuses.forEach(s => params.append('statuses', s));
  }
  if (filters.type) params.append('type', filters.type);
  if (filters.workStartDateFrom) params.append('workStartDateFrom', filters.workStartDateFrom);
  if (filters.workStartDateTo) params.append('workStartDateTo', filters.workStartDateTo);
  if (filters.expiresBeforeDate) params.append('expiresBeforeDate', filters.expiresBeforeDate);
  if (filters.expiringWithinDays !== undefined) params.append('expiringWithinDays', String(filters.expiringWithinDays));
  if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortDescending !== undefined) params.append('sortDescending', String(filters.sortDescending));

  const response = await apiClient.get<LocateTicketListResponse>(`/locate-tickets?${params.toString()}`);
  return response.data;
}

export async function getLocateTicket(id: string): Promise<LocateTicketDetail> {
  const response = await apiClient.get<LocateTicketDetail>(`/locate-tickets/${id}`);
  return response.data;
}

export async function createLocateTicket(data: CreateLocateTicketRequest): Promise<LocateTicketDetail> {
  const response = await apiClient.post<LocateTicketDetail>('/locate-tickets', data);
  return response.data;
}

export async function updateLocateTicket(
  id: string,
  data: UpdateLocateTicketRequest
): Promise<LocateTicketDetail> {
  const response = await apiClient.put<LocateTicketDetail>(`/locate-tickets/${id}`, data);
  return response.data;
}

export async function updateLocateTicketStatus(
  id: string,
  data: UpdateLocateTicketStatusRequest
): Promise<LocateTicketDetail> {
  const response = await apiClient.put<LocateTicketDetail>(`/locate-tickets/${id}/status`, data);
  return response.data;
}

export async function deleteLocateTicket(id: string): Promise<void> {
  await apiClient.delete(`/locate-tickets/${id}`);
}

export async function getLocateTicketStats(): Promise<LocateTicketStats> {
  const response = await apiClient.get<LocateTicketStats>('/locate-tickets/stats');
  return response.data;
}

export async function renewLocateTicket(id: string): Promise<LocateTicketDetail> {
  const response = await apiClient.post<LocateTicketDetail>(`/locate-tickets/${id}/renew`);
  return response.data;
}

export async function getLocateTicketsByDropBury(dropBuryId: string): Promise<LocateTicket[]> {
  const response = await apiClient.get<LocateTicket[]>(`/locate-tickets/by-drop-bury/${dropBuryId}`);
  return response.data;
}

export async function getLocateTicketsByMilestone(milestoneId: string): Promise<LocateTicket[]> {
  const response = await apiClient.get<LocateTicket[]>(`/locate-tickets/by-milestone/${milestoneId}`);
  return response.data;
}

// ==================== Helper Functions ====================

export function getStatusLabel(status: LocateTicketStatus): string {
  const labels: Record<LocateTicketStatus, string> = {
    Draft: 'Draft',
    Submitted: 'Submitted',
    Pending: 'Pending Response',
    PartiallyMarked: 'Partially Marked',
    AllClear: 'All Clear',
    Conflict: 'Conflict',
    Expired: 'Expired',
    Cancelled: 'Cancelled',
    Closed: 'Closed',
  };
  return labels[status] || status;
}

export function getStatusColor(status: LocateTicketStatus): string {
  const colors: Record<LocateTicketStatus, string> = {
    Draft: 'bg-gray-500/20 text-gray-400',
    Submitted: 'bg-blue-500/20 text-blue-400',
    Pending: 'bg-yellow-500/20 text-yellow-400',
    PartiallyMarked: 'bg-orange-500/20 text-orange-400',
    AllClear: 'bg-green-500/20 text-green-400',
    Conflict: 'bg-red-500/20 text-red-400',
    Expired: 'bg-red-500/20 text-red-400',
    Cancelled: 'bg-gray-500/20 text-gray-400',
    Closed: 'bg-green-500/20 text-green-400',
  };
  return colors[status] || 'bg-gray-500/20 text-gray-400';
}

export function getTypeLabel(type: LocateTicketType): string {
  const labels: Record<LocateTicketType, string> = {
    Normal: 'Normal',
    Priority: 'Priority',
    Emergency: 'Emergency',
    Renewal: 'Renewal',
  };
  return labels[type] || type;
}

export function getTypeColor(type: LocateTicketType): string {
  const colors: Record<LocateTicketType, string> = {
    Normal: 'bg-gray-500/20 text-gray-400',
    Priority: 'bg-yellow-500/20 text-yellow-400',
    Emergency: 'bg-red-500/20 text-red-400',
    Renewal: 'bg-blue-500/20 text-blue-400',
  };
  return colors[type] || 'bg-gray-500/20 text-gray-400';
}

export function getExcavationMethodLabel(method: ExcavationMethod): string {
  const labels: Record<ExcavationMethod, string> = {
    DirectionalBore: 'Directional Bore',
    Trenching: 'Trenching',
    HandDig: 'Hand Dig',
    VacuumExcavation: 'Vacuum Excavation',
    Plowing: 'Plowing',
    Aerial: 'Aerial',
    Other: 'Other',
  };
  return labels[method] || method;
}

/**
 * Parse excavation methods from Iowa OneCall raw string.
 * Input format: "Trenching: N    Boring:  Y    Backhoe:  Y"
 * Returns array of methods marked "Y"
 */
export function parseExcavationMethodsRaw(raw?: string): string[] {
  if (!raw) return [];

  const methods: string[] = [];
  const matches = raw.matchAll(/(\w+):\s*([YN])/gi);

  for (const match of matches) {
    if (match[2].toUpperCase() === 'Y') {
      methods.push(match[1]);
    }
  }

  return methods;
}

/**
 * Format time from TimeSpan string (e.g., "06:00:00" or "PT6H") to display format
 */
export function formatTimeDisplay(timeString?: string): string {
  if (!timeString) return '';

  // Handle ISO 8601 duration format (PT6H, PT6H30M, etc.)
  if (timeString.startsWith('PT')) {
    const hourMatch = timeString.match(/(\d+)H/);
    const minMatch = timeString.match(/(\d+)M/);
    const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
    const minutes = minMatch ? parseInt(minMatch[1]) : 0;

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;

    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  // Handle "HH:MM:SS" format (TimeSpan from C#)
  const parts = timeString.split(':');
  if (parts.length >= 2) {
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;

    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  return timeString;
}

export function getExpirationDisplay(expiresAt: string | undefined | null): { text: string; color: string; urgent: boolean } {
  if (!expiresAt) {
    return { text: 'No expiration', color: 'text-gray-400', urgent: false };
  }

  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { text: `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`, color: 'text-red-400', urgent: true };
  } else if (diffDays === 0) {
    return { text: 'Expires today', color: 'text-red-400', urgent: true };
  } else if (diffDays === 1) {
    return { text: 'Expires tomorrow', color: 'text-orange-400', urgent: true };
  } else if (diffDays <= 3) {
    return { text: `Expires in ${diffDays} days`, color: 'text-yellow-400', urgent: true };
  } else if (diffDays <= 7) {
    return { text: `Expires in ${diffDays} days`, color: 'text-blue-400', urgent: false };
  } else {
    return { text: `Expires in ${diffDays} days`, color: 'text-gray-400', urgent: false };
  }
}

export function formatDepthDisplay(inches: number): string {
  if (inches >= 12) {
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    if (remainingInches === 0) {
      return `${feet} ft`;
    }
    return `${feet} ft ${remainingInches} in`;
  }
  return `${inches} in`;
}

// ==================== Iowa One Call API ====================

/**
 * Get available Iowa One Call positive response status options
 */
export async function getIowaStatusOptions(): Promise<IowaStatusOption[]> {
  const response = await apiClient.get<IowaStatusOption[]>('/locate-tickets/iowa-status-options');
  return response.data;
}

/**
 * Post a positive response (status update) to Iowa One Call for a ticket
 */
export async function postIowaResponse(
  ticketId: string,
  data: PostIowaStatusRequest
): Promise<IowaTicketResponseResult> {
  const response = await apiClient.post<IowaTicketResponseResult>(
    `/locate-tickets/${ticketId}/iowa-response`,
    data
  );
  return response.data;
}

// ==================== Locate Ticket Photo API Functions ====================

/**
 * Get all photos for a locate ticket
 */
export async function getLocateTicketPhotos(ticketId: string): Promise<LocateTicketPhoto[]> {
  const response = await apiClient.get<LocateTicketPhoto[]>(`/locate-tickets/${ticketId}/photos`);
  return response.data;
}

/**
 * Get a single photo with download URL
 */
export async function getLocateTicketPhoto(ticketId: string, photoId: string): Promise<LocateTicketPhoto> {
  const response = await apiClient.get<LocateTicketPhoto>(`/locate-tickets/${ticketId}/photos/${photoId}`);
  return response.data;
}

/**
 * Upload a photo for a locate ticket
 */
export async function uploadLocateTicketPhoto(
  ticketId: string,
  file: File,
  options?: {
    category?: LocateTicketPhotoCategory;
    description?: string;
    latitude?: number;
    longitude?: number;
  }
): Promise<LocateTicketPhoto> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.category) {
    formData.append('category', options.category);
  }
  if (options?.description) {
    formData.append('description', options.description);
  }
  if (options?.latitude !== undefined) {
    formData.append('latitude', options.latitude.toString());
  }
  if (options?.longitude !== undefined) {
    formData.append('longitude', options.longitude.toString());
  }

  const response = await apiClient.post<LocateTicketPhoto>(
    `/locate-tickets/${ticketId}/photos`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Update photo metadata
 */
export async function updateLocateTicketPhoto(
  ticketId: string,
  photoId: string,
  data: UpdateLocateTicketPhotoRequest
): Promise<LocateTicketPhoto> {
  const response = await apiClient.put<LocateTicketPhoto>(
    `/locate-tickets/${ticketId}/photos/${photoId}`,
    data
  );
  return response.data;
}

/**
 * Delete a photo
 */
export async function deleteLocateTicketPhoto(ticketId: string, photoId: string): Promise<void> {
  await apiClient.delete(`/locate-tickets/${ticketId}/photos/${photoId}`);
}

/**
 * Format photo category for display
 */
export function formatLocateTicketPhotoCategory(category: LocateTicketPhotoCategory): string {
  const categoryLabels: Record<LocateTicketPhotoCategory, string> = {
    General: 'General',
    BeforeLocate: 'Before Locate',
    AfterLocate: 'After Locate',
    MarkedUtility: 'Marked Utility',
    Conflict: 'Conflict',
    Damage: 'Damage',
    WorkArea: 'Work Area',
    Other: 'Other',
  };
  return categoryLabels[category] || category;
}

/**
 * Photo category options for select dropdowns
 */
export const locateTicketPhotoCategoryOptions = [
  { value: 'General', label: 'General' },
  { value: 'BeforeLocate', label: 'Before Locate' },
  { value: 'AfterLocate', label: 'After Locate' },
  { value: 'MarkedUtility', label: 'Marked Utility' },
  { value: 'Conflict', label: 'Conflict' },
  { value: 'Damage', label: 'Damage' },
  { value: 'WorkArea', label: 'Work Area' },
  { value: 'Other', label: 'Other' },
] as const;

/**
 * Format file size for display
 */
export function formatPhotoFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
